import React from 'react';
import { Product, NewsItem, Business, CustomPage, BioLink, SiteSettings, NewsCategory, GuideCategory, WebStory } from '../types';
import { Helmet } from './Helmet';
import { Globe, BookOpen, ShoppingBag, Building2, FileText, Smartphone, User, ArrowRight, CheckCircle2 } from 'lucide-react';

interface SitemapPageProps {
  settings: SiteSettings;
  products: Product[];
  news: NewsItem[];
  businesses: Business[];
  customPages: CustomPage[];
  bioLinks: BioLink[];
  newsCategories: NewsCategory[];
  guideCategories: GuideCategory[];
  webStories: WebStory[];
  onNavigate: (page: string, param?: string) => void;
}

export const SitemapPage: React.FC<SitemapPageProps> = ({
  settings,
  products,
  news,
  businesses,
  customPages,
  bioLinks,
  newsCategories,
  guideCategories,
  webStories,
  onNavigate
}) => {
  const siteTitle = settings.siteTitle || "Portal de Conteúdo";

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet
        pageParam="sitemap"
        articleSlug={null}
        productSlug={null}
        categorySlug={null}
        customPageSlug={null}
        bioLinkSlug={null}
        storySlug={null}
        siteSlug={null}
        settings={settings}
        products={products}
        news={news}
        businesses={businesses}
        customPages={customPages}
        bioLinks={bioLinks}
        newsCategories={newsCategories}
        productCategories={[]}
        guideCategories={guideCategories}
        overrideTitle={`Mapa do Site (HTML Sitemap) | ${siteTitle}`}
        overrideDescription={`Navegue pelo mapa completo do site. Acesse todos os artigos, produtos, guia de empresas, páginas e histórias publicadas em ${siteTitle}.`}
      />

      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm text-center">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Globe size={32} />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-3">
            Mapa do Site (HTML Sitemap)
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            Índice completo de todas as seções, categorias, conteúdos, produtos e serviços publicados no portal <strong>{siteTitle}</strong>.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          
          {/* Main Pages */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                <Globe size={20} />
              </div>
              <h2 className="font-bold text-gray-900 text-lg">Páginas Institucionais</h2>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><a href="/" onClick={(e) => { e.preventDefault(); onNavigate('home'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Início</a></li>
              <li><a href="/blog" onClick={(e) => { e.preventDefault(); onNavigate('blog'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Blog de Notícias</a></li>
              <li><a href="/produtos" onClick={(e) => { e.preventDefault(); onNavigate('produtos'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Produtos & Soluções</a></li>
              <li><a href="/guia" onClick={(e) => { e.preventDefault(); onNavigate('guia'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Guia Comercial de Empresas</a></li>
              <li><a href="/webstories" onClick={(e) => { e.preventDefault(); onNavigate('webstories'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Web Stories</a></li>
              <li><a href="/quem-somos" onClick={(e) => { e.preventDefault(); onNavigate('quem-somos'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Quem Somos</a></li>
              <li><a href="/beneficios" onClick={(e) => { e.preventDefault(); onNavigate('beneficios'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Vantagens e Anúncios</a></li>
              <li><a href="/contato" onClick={(e) => { e.preventDefault(); onNavigate('contato'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Contato e Suporte</a></li>
              <li><a href="/privacidade" onClick={(e) => { e.preventDefault(); onNavigate('privacidade'); }} className="hover:text-blue-600 flex items-center gap-2"><ArrowRight size={14} className="text-blue-500" /> Política de Privacidade</a></li>
            </ul>
          </div>

          {/* News Categories */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
              <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
                <BookOpen size={20} />
              </div>
              <h2 className="font-bold text-gray-900 text-lg">Categorias do Blog ({newsCategories.length})</h2>
            </div>
            <ul className="space-y-2 text-sm text-gray-600 max-h-60 overflow-y-auto pr-1">
              {newsCategories.map((cat) => (
                <li key={cat.id}>
                  <a 
                    href={`/categoria/${cat.slug}`} 
                    onClick={(e) => { e.preventDefault(); onNavigate('blog', cat.slug); }} 
                    className="hover:text-emerald-600 flex items-center gap-2"
                  >
                    <CheckCircle2 size={12} className="text-emerald-500 shrink-0" /> {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
              <div className="p-2 bg-amber-50 text-amber-600 rounded-xl">
                <ShoppingBag size={20} />
              </div>
              <h2 className="font-bold text-gray-900 text-lg">Produtos ({products.length})</h2>
            </div>
            <ul className="space-y-2 text-sm text-gray-600 max-h-60 overflow-y-auto pr-1">
              {products.map((prod) => (
                <li key={prod.id}>
                  <a 
                    href={`/produtos/${prod.slug}`} 
                    onClick={(e) => { e.preventDefault(); onNavigate('produtos', prod.slug); }} 
                    className="hover:text-amber-600 flex items-center gap-2 truncate"
                  >
                    <ArrowRight size={12} className="text-amber-500 shrink-0" /> {prod.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Recent Articles */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
              <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                <BookOpen size={20} />
              </div>
              <h2 className="font-bold text-gray-900 text-lg">Artigos Recentes ({news.length})</h2>
            </div>
            <ul className="space-y-2 text-sm text-gray-600 max-h-60 overflow-y-auto pr-1">
              {news.slice(0, 30).map((art) => (
                <li key={art.id}>
                  <a 
                    href={`/${art.category || 'geral'}/${art.slug}`} 
                    onClick={(e) => { e.preventDefault(); onNavigate('blog', art.slug); }} 
                    className="hover:text-indigo-600 flex items-center gap-2 truncate"
                  >
                    <ArrowRight size={12} className="text-indigo-500 shrink-0" /> {art.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Directory Businesses */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
              <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
                <Building2 size={20} />
              </div>
              <h2 className="font-bold text-gray-900 text-lg">Guia de Empresas ({businesses.length})</h2>
            </div>
            <ul className="space-y-2 text-sm text-gray-600 max-h-60 overflow-y-auto pr-1">
              {businesses.slice(0, 30).map((bus) => (
                <li key={bus.id}>
                  <a 
                    href={`/guia/geral/${bus.slug}`} 
                    onClick={(e) => { e.preventDefault(); onNavigate('guia', bus.slug); }} 
                    className="hover:text-purple-600 flex items-center gap-2 truncate"
                  >
                    <ArrowRight size={12} className="text-purple-500 shrink-0" /> {bus.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Custom Pages */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
              <div className="p-2 bg-rose-50 text-rose-600 rounded-xl">
                <FileText size={20} />
              </div>
              <h2 className="font-bold text-gray-900 text-lg">Páginas Institucionais ({customPages.length})</h2>
            </div>
            <ul className="space-y-2 text-sm text-gray-600 max-h-60 overflow-y-auto pr-1">
              {customPages.map((page) => (
                <li key={page.id}>
                  <a 
                    href={`/p/${page.slug}`} 
                    onClick={(e) => { e.preventDefault(); onNavigate('p', page.slug); }} 
                    className="hover:text-rose-600 flex items-center gap-2 truncate"
                  >
                    <ArrowRight size={12} className="text-rose-500 shrink-0" /> {page.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Web Stories */}
          {webStories.length > 0 && (
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
              <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                <div className="p-2 bg-pink-50 text-pink-600 rounded-xl">
                  <Smartphone size={20} />
                </div>
                <h2 className="font-bold text-gray-900 text-lg">Web Stories ({webStories.length})</h2>
              </div>
              <ul className="space-y-2 text-sm text-gray-600 max-h-60 overflow-y-auto pr-1">
                {webStories.map((story) => (
                  <li key={story.id}>
                    <a 
                      href={`/story/${story.slug}`} 
                      onClick={(e) => { e.preventDefault(); onNavigate('story', story.slug); }} 
                      className="hover:text-pink-600 flex items-center gap-2 truncate"
                    >
                      <ArrowRight size={12} className="text-pink-500 shrink-0" /> {story.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Bio Links */}
          {bioLinks.length > 0 && (
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
              <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                <div className="p-2 bg-teal-50 text-teal-600 rounded-xl">
                  <User size={20} />
                </div>
                <h2 className="font-bold text-gray-900 text-lg">Páginas Bio ({bioLinks.length})</h2>
              </div>
              <ul className="space-y-2 text-sm text-gray-600 max-h-60 overflow-y-auto pr-1">
                {bioLinks.map((bio) => (
                  <li key={bio.id}>
                    <a 
                      href={`/bio/${bio.slug}`} 
                      onClick={(e) => { e.preventDefault(); onNavigate('bio', bio.slug); }} 
                      className="hover:text-teal-600 flex items-center gap-2 truncate"
                    >
                      <ArrowRight size={12} className="text-teal-500 shrink-0" /> {bio.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
